/**
 * Утилита для извлечения доминирующих цветов из изображений
 * и применения их к градиентам карточек игр
 */

/**
 * Извлекает доминирующий цвет из изображения используя Canvas API
 * @param {HTMLImageElement} img - Элемент изображения
 * @returns {Promise<string>} - Цвет в формате rgba()
 */
export const extractDominantColor = (img) => {
    return new Promise((resolve) => {
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Уменьшаем размер для ускорения анализа
            const sampleSize = 50;
            canvas.width = sampleSize;
            canvas.height = sampleSize;

            // Рисуем уменьшенное изображение
            ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

            const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
            const data = imageData.data;

            const colorData = {};

            // Анализируем каждый пиксель
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const a = data[i + 3];

                // Пропускаем прозрачные пиксели
                if (a < 125) continue;

                // Пропускаем слишком темные цвета (черноту)
                const brightness = (r + g + b) / 3;
                if (brightness < 60) continue; // Игнорируем темные цвета

                // Пропускаем слишком серые цвета (низкая насыщенность)
                const max = Math.max(r, g, b);
                const min = Math.min(r, g, b);
                const saturation = max === 0 ? 0 : (max - min) / max;
                if (saturation < 0.2) continue; // Игнорируем слишком серые цвета

                // Группируем похожие цвета (квантизация)
                const quantizedR = Math.round(r / 32) * 32;
                const quantizedG = Math.round(g / 32) * 32;
                const quantizedB = Math.round(b / 32) * 32;

                const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;

                if (!colorData[colorKey]) {
                    colorData[colorKey] = {
                        count: 0,
                        r: quantizedR,
                        g: quantizedG,
                        b: quantizedB
                    };
                }
                colorData[colorKey].count++;
            }

            // Комбинированный подход: частота + яркость + насыщенность
            let dominantColor = '0,0,0';
            let maxScore = 0;

            for (const [colorKey, data] of Object.entries(colorData)) {
                const { count, r, g, b } = data;

                // Вычисляем яркость (luminance)
                const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

                // Вычисляем насыщенность
                const max = Math.max(r, g, b) / 255;
                const min = Math.min(r, g, b) / 255;
                const saturation = max === 0 ? 0 : (max - min) / max;

                // Комбинированный счет: частота × яркость × насыщенность
                // Сильно предпочитаем яркие и сочные цвета
                const frequencyWeight = count / (sampleSize * sampleSize); // Нормализованная частота
                const brightnessWeight = Math.pow(brightness, 0.5); // Еще больше предпочитаем яркие
                const saturationWeight = Math.pow(saturation + 0.2, 2.0); // Сильно предпочитаем насыщенные

                const score = frequencyWeight * brightnessWeight * saturationWeight;

                if (score > maxScore) {
                    maxScore = score;
                    dominantColor = colorKey;
                }
            }

            const [r, g, b] = dominantColor.split(',').map(Number);

            // Создаем яркий сочный цвет без черноты
            let enhancedR = r;
            let enhancedG = g;
            let enhancedB = b;

            // Убираем черноту - поднимаем минимальные значения
            const minBrightness = 80; // Минимальное значение для каждого канала
            enhancedR = Math.max(enhancedR, minBrightness);
            enhancedG = Math.max(enhancedG, minBrightness);
            enhancedB = Math.max(enhancedB, minBrightness);

            // Увеличиваем насыщенность для сочности
            const maxVal = Math.max(enhancedR, enhancedG, enhancedB);
            const minVal = Math.min(enhancedR, enhancedG, enhancedB);

            if (maxVal > minVal) {
                // Формула увеличения насыщенности
                const saturationBoost = 1.4; // Увеличиваем насыщенность в 1.4 раза
                const center = (maxVal + minVal) / 2;

                enhancedR = Math.round(center + (enhancedR - center) * saturationBoost);
                enhancedG = Math.round(center + (enhancedG - center) * saturationBoost);
                enhancedB = Math.round(center + (enhancedB - center) * saturationBoost);
            }

            // Ограничиваем значения диапазоном 80-255 для яркости без черноты
            enhancedR = Math.min(255, Math.max(80, enhancedR));
            enhancedG = Math.min(255, Math.max(80, enhancedG));
            enhancedB = Math.min(255, Math.max(80, enhancedB));

            resolve(`rgb(${enhancedR}, ${enhancedG}, ${enhancedB})`); // Яркий сочный цвет

        } catch (error) {
            console.warn('Ошибка извлечения цвета:', error);
            // Возвращаем яркий цвет при ошибке вместо черного
            resolve('rgb(100, 120, 140)'); // Приятный сине-серый вместо черного
        }
    });
};

/**
 * Применяет динамический цвет градиента к карточке игры
 * @param {HTMLElement} cardElement - Элемент карточки игры
 * @param {HTMLImageElement} imgElement - Элемент изображения
 */
export const applyDynamicGradient = async (cardElement, imgElement) => {
    try {
        // Ждем полной загрузки изображения
        if (!imgElement.complete) {
            await new Promise((resolve) => {
                imgElement.onload = resolve;
                imgElement.onerror = resolve;
            });
        }

        const dominantColor = await extractDominantColor(imgElement);

        // Применяем цвет через CSS переменную
        cardElement.style.setProperty('--game-gradient-color', dominantColor);

    } catch (error) {
        console.warn('Ошибка применения динамического градиента:', error);
    }
};

/**
 * Инициализирует динамические градиенты для всех карточек на странице
 */
export const initializeDynamicGradients = () => {
    const gameCards = document.querySelectorAll('.universal-game-card');

    gameCards.forEach(card => {
        const img = card.querySelector('.universal-game-image');
        if (img) {
            applyDynamicGradient(card, img);
        }
    });
};

/**
 * Добавляет обработчик для новых карточек (для динамически загружаемого контента)
 */
export const setupDynamicGradientObserver = () => {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) { // Element node
                    // Проверяем добавленные карточки
                    const newCards = node.classList?.contains('universal-game-card')
                        ? [node]
                        : node.querySelectorAll?.('.universal-game-card') || [];

                    newCards.forEach(card => {
                        const img = card.querySelector('.universal-game-image');
                        if (img) {
                            applyDynamicGradient(card, img);
                        }
                    });
                }
            });
        });
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    return observer;
};
